import { CalendarDays, ChevronDown, ExternalLink, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { BOOKING_OPTIONS, callDurationFromQuery, type CallDuration, type ScheduleAccess } from "../../shared/scheduling";
import { ApiError, getScheduleAccess, unlockSchedule } from "../api";
import { TurnstileWidget } from "./TurnstileWidget";

type Props = { siteKey: string; theme: "light" | "dark" };

export function ScheduleCalendar({ booking, duration, theme }: { booking: ScheduleAccess["bookings"][CallDuration]; duration: CallDuration; theme: Props["theme"] }) {
  return (
    <div className="schedule-calendar" data-theme={theme}>
      <iframe src={booking.embedUrl} title={`Book a ${duration}-minute call with Omer`} referrerPolicy="no-referrer" />
    </div>
  );
}

export function SchedulePage({ siteKey, theme }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const duration = callDurationFromQuery(searchParams.get("duration"));
  const [access, setAccess] = useState<ScheduleAccess | null>(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [resetKey, setResetKey] = useState(0);
  const onToken = useCallback((value: string) => setToken(value), []);
  const booking = access?.bookings[duration];

  useEffect(() => {
    let cancelled = false;
    void getScheduleAccess().then((result) => {
      if (!cancelled) setAccess(result);
    }).catch((cause: unknown) => {
      if (!cancelled && !(cause instanceof ApiError && cause.code === "turnstile_required")) {
        setError(cause instanceof Error ? cause.message : "Scheduling could not load. Please try again.");
      }
    }).finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!access) return;
    const timeout = window.setTimeout(() => {
      setAccess(null);
      setToken("");
      setError("Your access has expired. Complete a new security check to continue.");
      setResetKey((value) => value + 1);
    }, Math.max(0, Date.parse(access.expiresAt) - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [access]);

  async function verifyAccess(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      setAccess(await unlockSchedule(token));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The security check could not be verified. Please try again.");
      setToken("");
      setResetKey((value) => value + 1);
    } finally {
      setSubmitting(false);
    }
  }

  function selectDuration(nextDuration: CallDuration) {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("duration", String(nextDuration));
    setSearchParams(nextParams);
  }

  return (
    <section className="schedule-page" aria-labelledby="schedule-title">
      <header className="schedule-heading">
        <div>
          <span className="eyebrow"><CalendarDays size={15} aria-hidden="true" /> Let’s talk</span>
          <h1 id="schedule-title">Schedule a call</h1>
          <p>Choose a time for a Google Meet call with Omer.</p>
        </div>
        {booking && <a className="schedule-external-link" href={booking.url} target="_blank" rel="noreferrer">
          Open in Google Calendar <ExternalLink size={15} aria-hidden="true" />
        </a>}
      </header>
      <div className="schedule-controls">
        <label htmlFor="call-duration">Call length</label>
        <div className="schedule-duration">
          <select id="call-duration" value={duration} onChange={(event) => selectDuration(Number(event.target.value) as CallDuration)}>
            {Object.entries(BOOKING_OPTIONS).map(([value, option]) => <option key={value} value={value}>{option.label}</option>)}
          </select>
          <ChevronDown size={16} aria-hidden="true" />
        </div>
      </div>
      {booking ? <>
        <ScheduleCalendar booking={booking} duration={duration} theme={theme} />
        <p className="schedule-note">Booking is handled by Google Calendar. If the calendar doesn’t load, use the link above to open it in a new tab.</p>
      </> : <form className="schedule-access" onSubmit={(event) => void verifyAccess(event)} aria-busy={checking || submitting}>
        <ShieldCheck size={26} aria-hidden="true" />
        <h2>A quick security check</h2>
        <p>Help keep available times open for real conversations.</p>
        {checking ? <p role="status">Checking access…</p> : <>
          <TurnstileWidget siteKey={siteKey} action="schedule" theme={theme} onToken={onToken} resetKey={resetKey} />
          <button className="primary-action" type="submit" disabled={!token || submitting}>{submitting ? "Verifying…" : "View available times"}</button>
        </>}
        {error && <p className="form-error" role="alert">{error}</p>}
      </form>}
    </section>
  );
}
